import * as React from 'react';

import './index.scss';

interface ITextareaProps {
  left: number;
  top: number;
  text: string;
  onChangeText: (text: string) => void;
  onBlur?: () => void;
  onSubmit?: () => void;
  fontSize?: number;
  color?: string;
  fontFamily?: string;
}

const Textarea: React.FC<ITextareaProps> = (props: ITextareaProps) => {
  const textRef = React.useRef<HTMLTextAreaElement | null>(null);
  const measureCanvasCtxRef = React.useRef<CanvasRenderingContext2D | null>(null);

  const updateTextareaSize = React.useCallback(() => {
    const textarea = textRef.current;
    if (!textarea) {
      return;
    }

    const computedStyle = window.getComputedStyle(textarea);
    if (!measureCanvasCtxRef.current) {
      const measureCanvas = window.document.createElement('canvas');
      measureCanvasCtxRef.current = measureCanvas.getContext('2d');
    }
    const measureCtx = measureCanvasCtxRef.current;

    const lines = props.text.split('\n');
    let contentWidth = 0;
    if (measureCtx) {
      measureCtx.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
      for (const line of lines) {
        const lineWidth = measureCtx.measureText(line || ' ').width;
        if (lineWidth > contentWidth) {
          contentWidth = lineWidth;
        }
      }
    } else {
      contentWidth = Math.max(...lines.map(line => line.length), 1) * 8;
    }

    const canvas = window.document.querySelector('canvas.app');
    const canvasRect = canvas instanceof HTMLCanvasElement ? canvas.getBoundingClientRect() : null;
    const safeRightEdge = canvasRect ? canvasRect.right - 12 : window.innerWidth - 12;
    const availableWidth = Math.max(120, safeRightEdge - props.left);
    const nextWidth = Math.min(Math.max(Math.ceil(contentWidth) + 12, 24), availableWidth);

    textarea.style.width = `${nextWidth}px`;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [props.left, props.text]);

  React.useEffect(() => {
    if (textRef) {
      textRef.current?.focus();
      textRef.current?.select();
    }
  }, []);

  React.useLayoutEffect(() => {
    updateTextareaSize();
  }, [updateTextareaSize]);
  
  const style: React.CSSProperties = {
    left: props.left,
    top: props.top,
  };

  if (props.fontSize) style.fontSize = props.fontSize;
  if (props.color) style.color = props.color; // Note: 'color' sets text color
  if (props.fontFamily) style.fontFamily = props.fontFamily;

  return (
    <textarea
      className="textarea"
      ref={textRef}
      style={style}
      value={props.text}
      onBlur={props.onBlur}
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
          props.onSubmit?.();
          e.preventDefault();
        }
        e.stopPropagation(); // Prevent triggering app-level shortcuts
      }}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
        props.onChangeText(e.target.value);
      }}
    />
  );
};

export default Textarea;
